import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { PageLayout } from '../components/Layout';
import { ArrowLeft, Calendar } from 'lucide-react';
import { Button } from '../components/ui/button';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  } catch {
    return '';
  }
};

const BlogPostPage = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    fetch(`${BACKEND}/api/blog/posts/${slug}`)
      .then(async (r) => {
        if (!r.ok) {
          setNotFound(true);
          setLoading(false);
          return null;
        }
        return r.json();
      })
      .then((data) => {
        if (data) {
          setPost(data);
          document.title = `${data.title} | TryWeb`;
        }
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <PageLayout>
        <section className="py-24">
          <div className="max-w-3xl mx-auto px-4 text-muted-foreground">Loading…</div>
        </section>
      </PageLayout>
    );
  }

  if (notFound || !post) {
    return (
      <PageLayout>
        <section className="py-24 hero-glow">
          <div className="max-w-3xl mx-auto px-4 text-center">
            <h1 className="text-3xl font-bold mb-4">Post not found</h1>
            <p className="text-muted-foreground mb-8">
              That post might have moved or never existed.
            </p>
            <Link to="/blog">
              <Button variant="outline" className="gap-2" data-testid="blog-back-cta">
                <ArrowLeft className="w-4 h-4" /> Back to blog
              </Button>
            </Link>
          </div>
        </section>
      </PageLayout>
    );
  }

  const cover = post.cover_image_url
    ? post.cover_image_url.startsWith('http')
      ? post.cover_image_url
      : `${BACKEND}${post.cover_image_url}`
    : null;

  return (
    <PageLayout>
      <article>
        <section className="pt-24 pb-12 hero-glow">
          <div className="max-w-3xl mx-auto px-4 sm:px-6">
            <Link to="/blog" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-8" data-testid="blog-back-link">
              <ArrowLeft className="w-4 h-4" /> All posts
            </Link>
            <div className="flex items-center gap-3 text-xs text-muted-foreground mb-4">
              <span className="px-2.5 py-1 rounded-full bg-primary/15 text-primary font-medium uppercase tracking-wide">
                {post.topic?.replace(/-/g, ' ')}
              </span>
              <span className="flex items-center gap-1.5">
                <Calendar className="w-3.5 h-3.5" />
                {formatDate(post.published_at)}
              </span>
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold leading-tight mb-6">{post.title}</h1>
            <p className="text-xl text-muted-foreground">{post.excerpt}</p>
          </div>
        </section>

        {cover && (
          <section className="pb-12">
            <div className="max-w-4xl mx-auto px-4 sm:px-6">
              <div className="rounded-3xl overflow-hidden border border-white/5">
                <img
                  src={cover}
                  alt={post.title}
                  className="w-full h-auto"
                  loading="eager"
                />
              </div>
            </div>
          </section>
        )}

        <section className="pb-24">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 prose-blog">
            <ReactMarkdown
              components={{
                h2: ({ node, ...p }) => <h2 className="text-2xl sm:text-3xl font-bold mt-12 mb-4" {...p} />,
                h3: ({ node, ...p }) => <h3 className="text-xl sm:text-2xl font-semibold mt-10 mb-3" {...p} />,
                p: ({ node, ...p }) => <p className="text-base leading-relaxed text-foreground/90 mb-5" {...p} />,
                a: ({ node, ...p }) => <a className="text-primary underline-offset-2 hover:underline" target="_blank" rel="noopener noreferrer" {...p} />,
                ul: ({ node, ...p }) => <ul className="list-disc pl-6 mb-5 space-y-2 text-foreground/90" {...p} />,
                ol: ({ node, ...p }) => <ol className="list-decimal pl-6 mb-5 space-y-2 text-foreground/90" {...p} />,
                li: ({ node, ...p }) => <li className="leading-relaxed" {...p} />,
                blockquote: ({ node, ...p }) => <blockquote className="border-l-4 border-primary/40 pl-4 italic text-muted-foreground my-6" {...p} />,
                code: ({ node, inline, ...p }) =>
                  inline ? (
                    <code className="bg-card/80 rounded px-1.5 py-0.5 text-sm" {...p} />
                  ) : (
                    <pre className="bg-card/80 rounded-lg p-4 overflow-x-auto text-sm my-6">
                      <code {...p} />
                    </pre>
                  ),
              }}
            >
              {post.content_md}
            </ReactMarkdown>

            {!!post.tags?.length && (
              <div className="mt-12 pt-8 border-t border-white/5 flex flex-wrap gap-2">
                {post.tags.map((t) => (
                  <span key={t} className="text-xs px-2.5 py-1 rounded-full bg-white/5 text-muted-foreground">
                    {t}
                  </span>
                ))}
              </div>
            )}
          </div>
        </section>
      </article>
    </PageLayout>
  );
};

export default BlogPostPage;
